/**
 * Intents de voz del consultorio (navegación, consultas y agenda).
 */
import {
  parseNaturalSchedule,
  parseCitasVoiceChunk,
  softenVoiceText,
  isTodayCitasQuery,
} from '../hooks/useCitasVoiceAssistant';
import { getCitaPacienteLabel, isCitaPacientePendiente } from './citasHelpers';

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const n = new Date();
  return d.getFullYear() === n.getFullYear()
    && d.getMonth() === n.getMonth()
    && d.getDate() === n.getDate();
}

function matchPacienteByName(spoken, pacientes) {
  const q = softenVoiceText(spoken);
  if (!q || !pacientes?.length) return null;
  const words = q.split(/\s+/).filter((w) => w.length > 1);
  let best = null;
  let bestScore = 0;
  for (const p of pacientes) {
    const n = softenVoiceText(p.nombre || '');
    if (!n) continue;
    if (n === q || n.includes(q) || q.includes(n)) return p;
    let score = 0;
    for (const w of words) {
      if (n.includes(w)) score += w.length;
    }
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  return bestScore >= 3 ? best : null;
}

function extractNameAfter(soft, verbs) {
  for (const v of verbs) {
    const re = new RegExp(`\\b${v}\\s+(.+)$`);
    const m = soft.match(re);
    if (m?.[1]) {
      return m[1]
        .replace(/\b(de|del|la|el|los|las|paciente|ficha|informacion|datos)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
  }
  return null;
}

const HELP_TEXT = [
  'Puedo: agendar citas, decir cuántas citas hay hoy,',
  'abrir o informar de un paciente, ver productos o facturas,',
  'e indicarte cómo usar el consultorio.',
  'Ej.: «agendar cita para el jueves», «citas de hoy»,',
  '«información del paciente Ana», «abrir productos», «ayuda».',
].join(' ');

/**
 * @returns {{ type: string, feedback?: string, path?: string, patch?: object, needsConfirm?: boolean, summary?: string, patientId?: string, clearConfirm?: boolean } | null}
 */
export function parseConsultorioVoiceIntent(text, ctx = {}) {
  const raw = (text || '').trim();
  const soft = softenVoiceText(raw);
  if (!soft) return null;

  const {
    pacientes = [],
    citas = [],
    productos = [],
    facturas = [],
    awaitingConfirm = false,
    modalOpen = false,
    equipo = [],
  } = ctx;

  // Confirmación de agenda pendiente
  if (awaitingConfirm || modalOpen) {
    const citaCmd = parseCitasVoiceChunk(raw, {
      modalOpen: true,
      pacientes,
      equipo,
      awaitingConfirm,
    });
    if (citaCmd && citaCmd.type !== 'NONE' && citaCmd.type !== 'STATUS') {
      return { ...citaCmd, scope: 'cita' };
    }
  }

  // Ayuda / indicaciones de uso
  if (
    /^(ayuda|indicaciones|instrucciones|que puedes hacer|que podes hacer|menu de voz)$/.test(soft)
    || /\b(ayuda|indicaciones|como\s+uso|que\s+puedo\s+decir)\b/.test(soft) && soft.split(/\s+/).length <= 6
  ) {
    return { type: 'HELP', feedback: HELP_TEXT };
  }

  // Citas de hoy / cuántas («dame la cita de hoy», Whisper: «de me la cita…»)
  if (isTodayCitasQuery(raw) || isTodayCitasQuery(soft)) {
    const today = (citas || []).filter((c) => isToday(c.fechaHora) && c.estado !== 'cancelada');
    const lines = today.slice(0, 5).map((c) => {
      const t = new Date(c.fechaHora).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
      const name = getCitaPacienteLabel(c, pacientes);
      const tag = isCitaPacientePendiente(c) ? ' (nuevo)' : '';
      return `${t} ${name}${tag}`;
    });
    const extra = today.length > 5 ? ` y ${today.length - 5} más` : '';
    const detail = lines.length
      ? ` ${lines.join('; ')}${extra}.`
      : ' No hay citas programadas para hoy.';
    return {
      type: 'SPEAK_NAV',
      path: '/consultorio/citas',
      feedback: `Hoy hay ${today.length} cita${today.length === 1 ? '' : 's'}.${detail}`,
    };
  }

  // Productos / inventario
  if (
    /\b(abrir|ver|mostrar|ir\s+a|vamos\s+a)?\s*(los\s+)?productos\b/.test(soft)
    || /\binventario\b/.test(soft)
    || /\bstock\b/.test(soft)
  ) {
    const n = productos?.length || 0;
    const bajos = (productos || []).filter((p) => {
      const stock = Number(p.stock ?? p.cantidad ?? 0);
      const min = Number(p.stockMinimo ?? p.minimo ?? 0);
      return min > 0 && stock <= min;
    }).length;
    return {
      type: 'SPEAK_NAV',
      path: '/consultorio/productos',
      feedback: bajos
        ? `Hay ${n} producto${n === 1 ? '' : 's'} en inventario. ${bajos} con stock bajo.`
        : `Hay ${n} producto${n === 1 ? '' : 's'} en inventario.`,
    };
  }

  // Facturas
  if (
    /\b(abrir|ver|mostrar|ir\s+a)?\s*(las\s+)?facturas?\b/.test(soft)
    || /\bfacturacion\b/.test(soft)
  ) {
    const n = facturas?.length || 0;
    const pend = (facturas || []).filter((f) => (f.estado || '').toLowerCase() === 'pendiente').length;
    return {
      type: 'SPEAK_NAV',
      path: '/consultorio/facturas',
      feedback: pend
        ? `Hay ${n} factura${n === 1 ? '' : 's'}. ${pend} pendiente${pend === 1 ? '' : 's'}.`
        : `Hay ${n} factura${n === 1 ? '' : 's'}.`,
    };
  }

  // Pacientes lista
  if (
    /^(abrir|ver|mostrar|ir\s+a)\s+pacientes$/.test(soft)
    || /\blista\s+de\s+pacientes\b/.test(soft)
    || soft === 'pacientes'
  ) {
    return {
      type: 'NAVIGATE',
      path: '/consultorio/pacientes',
      feedback: `Lista de pacientes (${pacientes.length}).`,
    };
  }

  // Calendario / citas
  if (
    /^(abrir|ver|mostrar|ir\s+a)\s+(el\s+)?(calendario|citas)$/.test(soft)
    || soft === 'calendario'
    || soft === 'citas'
  ) {
    return {
      type: 'NAVIGATE',
      path: '/consultorio/citas',
      feedback: 'Abriendo el calendario.',
    };
  }

  // Dashboard
  if (
    /^(abrir|ver|ir\s+a)\s+(el\s+)?(dashboard|inicio|panel)$/.test(soft)
    || soft === 'dashboard'
    || soft === 'inicio'
  ) {
    return {
      type: 'NAVIGATE',
      path: '/consultorio',
      feedback: 'Abriendo el panel del consultorio.',
    };
  }

  // Información / ficha de paciente
  if (
    /\b(informacion|datos|ficha|ver)\b.*\bpaciente\b/.test(soft)
    || /\bpaciente\b.*\b(informacion|datos|ficha)\b/.test(soft)
    || /\babrir\s+(la\s+)?ficha\b/.test(soft)
    || /\bver\s+paciente\b/.test(soft)
  ) {
    const name = extractNameAfter(soft, [
      'paciente',
      'ficha de',
      'ficha del',
      'informacion de',
      'informacion del',
      'datos de',
      'datos del',
      'ver paciente',
    ]) || extractNameAfter(soft, ['de']);
    const hit = name ? matchPacienteByName(name, pacientes) : null;
    if (hit) {
      const tel = hit.telefono || hit.celular || 'sin teléfono';
      const ced = hit.cedula || hit.documento || 'sin cédula';
      return {
        type: 'SPEAK_NAV',
        path: `/consultorio/paciente/${hit.id}`,
        patientId: String(hit.id),
        feedback: `${hit.nombre}. Tel: ${tel}. Doc: ${ced}. Abriendo la ficha.`,
      };
    }
    return {
      type: 'STATUS',
      feedback: name
        ? `No encontré al paciente «${name}». Probá otro nombre o abrí Pacientes.`
        : 'Decime el nombre: «información del paciente Ana».',
    };
  }

  // Agenda natural (cita)
  const natural = parseNaturalSchedule(raw, { pacientes, equipo });
  if (natural) {
    return { ...natural, scope: 'cita' };
  }

  // Comandos cortos de cita ya con modal
  const citaCmd = parseCitasVoiceChunk(raw, {
    modalOpen,
    pacientes,
    equipo,
    awaitingConfirm,
  });
  if (citaCmd && citaCmd.type !== 'NONE' && !(citaCmd.type === 'STATUS' && !modalOpen)) {
    // Si pide nueva cita corta
    if (citaCmd.type === 'OPEN_MODAL') {
      return { type: 'OPEN_CITA', feedback: citaCmd.feedback || 'Abriendo nueva cita…', patch: {} };
    }
    if (citaCmd.type !== 'STATUS' || modalOpen) {
      return { ...citaCmd, scope: 'cita' };
    }
  }

  if (/\bcita\b|\bagendar\b|\bagenda\b/.test(soft)) {
    return {
      type: 'STATUS',
      feedback: 'Ej.: «agendar cita para el jueves» o «agendar cita para Ana de 2 a 3».',
    };
  }

  return {
    type: 'HELP',
    feedback: `No entendí «${raw.slice(0, 60)}». ${HELP_TEXT}`,
  };
}

export { HELP_TEXT };
